import type { Models } from "api/types";
import type { AdvertisedOffer, PendingCartItem } from "@/modules/anonymousBrowsing";

/**
 * What changed between the prices a logged-out visitor was quoted and the prices their account actually gets.
 *
 * This is a pure diff, never a second pricing engine: the server has already decided what applies by the time the
 * cart comes back. All this does is explain the delta against what the app promised while it did not know who the
 * visitor was.
 */
export type PricingReconciliation = {
  /** Sum of the quoted line prices the visitor saw. */
  estimatedSubtotalInCents: number;
  /** `OrderSummaryItems` → `SubTotal` from the real cart. */
  subtotalInCents: number;
  /** Offers advertised while anonymous that the cart did not apply. */
  removedOffers: { title: string; reason: string | null }[];
  /** `OrderSummaryItems` → `MembershipDiscount`. */
  membershipDiscountInCents: number;
  repeatCash: { balanceInCents: number; eligibleInCents: number } | null;
  redeemableRewardCount: number;
  /** True when the cart ended up at a different location than the one the visitor priced. */
  locationChanged: boolean;
  /** Titles of items that could not be replayed into the real cart. */
  itemsNotAdded: string[];
};

/**
 * True when the cart's subtotal is not the one the visitor was quoted.
 *
 * The subtotal is the backstop for every reason a price can move that leaves no other trace: location-specific list
 * prices, a discount the cart declined without reporting it in `skippedPromoOffers`, a stale quote. Those cases
 * change no offer, no membership line and no item count, so without this the totals silently disagree with what the
 * visitor was shown.
 *
 * Gated on having quoted something. A visitor who added no items has an estimate of zero, and an existing account can
 * arrive at checkout with a cart of its own — comparing against zero there would report "your price changed" for a
 * cart the visitor never priced.
 */
export const hasSubtotalChange = (reconciliation: PricingReconciliation): boolean =>
  reconciliation.estimatedSubtotalInCents > 0 &&
  reconciliation.subtotalInCents !== reconciliation.estimatedSubtotalInCents;

/** True when there is something worth telling the patient about. */
export const hasReconciliationNews = (reconciliation: PricingReconciliation): boolean =>
  reconciliation.removedOffers.length > 0 ||
  reconciliation.membershipDiscountInCents > 0 ||
  reconciliation.repeatCash !== null ||
  reconciliation.redeemableRewardCount > 0 ||
  reconciliation.locationChanged ||
  reconciliation.itemsNotAdded.length > 0 ||
  hasSubtotalChange(reconciliation);

export const reconcileAnonymousQuote = (
  quote: PendingCartItem[],
  cart: Models<"ShopDisplayOrderResult">,
  failed: PendingCartItem[],
  browseLocationId: string | null,
  claimedOffers: AdvertisedOffer[] = [],
): PricingReconciliation => {
  const applied = new Set(
    (cart.discountSummary?.discounts ?? []).map((discount) => discount.discountId),
  );

  // The cart says which promo offers it skipped and why. Anything else we can only report neutrally: a discount can
  // be missing because it was already used, superseded by a better member price, globally exhausted or gated to
  // another location, and the payload does not distinguish those.
  const skippedReasons = new Map<string, string | null>(
    (cart.skippedPromoOffers ?? [])
      .filter((offer: { code?: string | null }) => !!offer.code)
      .map((offer: { code?: string | null; reason?: string | null }) => [
        offer.code as string,
        offer.reason ?? null,
      ]),
  );

  // Keyed by discount id, which is what the cart reports back, but the promo code is kept alongside
  // because that is the key the server's skipped-offer reasons arrive under.
  const advertised = new Map<string, { title: string; promoCodeValue?: string }>();
  for (const item of quote) {
    for (const discount of item.quote?.advertisedDiscounts ?? []) {
      advertised.set(discount.id, {
        title: discount.title,
        promoCodeValue: discount.promoCodeValue,
      });
    }
  }
  // An offer claimed with nothing to add is advertised just as loudly, so it has to be reported the
  // same way when the cart turns out not to accept it.
  for (const offer of claimedOffers) {
    advertised.set(offer.id, { title: offer.title, promoCodeValue: offer.promoCodeValue });
  }

  const summaryItem = (name: string) =>
    cart.orderSummaryItems?.find((item) => item.name === name)?.value ?? 0;

  const repeatCashBalance = cart.platformUser?.repeatCashBalance ?? 0;

  return {
    estimatedSubtotalInCents: quote.reduce(
      (sum, item) => sum + (item.quote?.displayedPriceInCents ?? 0) * (item.lineItem.quantity ?? 1),
      0,
    ),
    subtotalInCents: summaryItem("SubTotal"),
    removedOffers: [...advertised]
      .filter(([discountId]) => !applied.has(discountId))
      .map(([, offer]) => ({
        title: offer.title,
        // By promo code, not title: skippedPromoOffers is keyed by the code, so looking the reason up
        // by the offer's display name silently missed every time and the patient only ever saw the
        // generic "not available" line.
        reason: offer.promoCodeValue ? (skippedReasons.get(offer.promoCodeValue) ?? null) : null,
      })),
    membershipDiscountInCents: summaryItem("MembershipDiscount"),
    repeatCash:
      repeatCashBalance > 0
        ? {
            balanceInCents: repeatCashBalance,
            eligibleInCents: cart.repeatCashEligibleAmountInCents ?? 0,
          }
        : null,
    redeemableRewardCount: cart.rewardSummary?.redeemableRewardCount ?? 0,
    locationChanged: !!browseLocationId && cart.selectedLocationId !== browseLocationId,
    itemsNotAdded: failed.map((item) => item.title),
  };
};

const RECONCILIATION_KEY = "anonymousCheckout.reconciliation";

/**
 * Held in sessionStorage rather than router state because the trip from "verified" to the checkout render can pass
 * through an onboarding redirect for an existing account with an incomplete profile.
 */
export const writeReconciliation = (reconciliation: PricingReconciliation): void => {
  try {
    sessionStorage.setItem(RECONCILIATION_KEY, JSON.stringify(reconciliation));
  } catch {
    // A full or unavailable sessionStorage costs the explanation, not the checkout.
  }
};

export const consumeReconciliation = (): PricingReconciliation | null => {
  try {
    const raw = sessionStorage.getItem(RECONCILIATION_KEY);
    sessionStorage.removeItem(RECONCILIATION_KEY);
    return raw ? (JSON.parse(raw) as PricingReconciliation) : null;
  } catch {
    return null;
  }
};

export const clearReconciliation = (): void => {
  try {
    sessionStorage.removeItem(RECONCILIATION_KEY);
  } catch {
    // no-op
  }
};
